import { Box, Card } from "@mui/material";
import React, { useMemo, useRef } from "react";
import Chart from 'react-apexcharts';
import FormateCurrentcy from "../../utils/FormateCurrency";
import { useChartWidth } from "./useChartWidth";

const toSafeNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const clampHeight = (height, fallback = 320) => {
    const num = Number(height);
    if (!Number.isFinite(num) || num <= 0) return fallback;
    return Math.min(Math.max(num, 250), 420);
};

export default function TreeMap({ label = [], series = [], height = 320 }) {
    const containerRef = useRef(null);
    const chartWidth = useChartWidth(containerRef);
    const boxHeight = clampHeight(height);
    const plotHeight = Math.max(boxHeight - 16, 200);

    const allZero = label.length > 0 && label.every((_, index) => toSafeNumber(series[index]) === 0);

    const chartSeries = useMemo(() => ([{
        data: label
            .map((entry, index) => ({
                x: entry,
                y: allZero ? 1 : toSafeNumber(series[index]),
            }))
            .filter((entry) => allZero || entry.y !== 0),
    }]), [label, series, allZero]);

    const options = useMemo(() => ({
        legend: { show: false },
        chart: {
            sparkline: { enabled: false },
            type: 'treemap',
            foreColor: '#fff',
            toolbar: { show: false },
            animations: { enabled: false },
        },
        tooltip: {
            style: { fontSize: '12px', color: '#000' },
            y: {
                formatter: (val) => FormateCurrentcy(allZero ? 0 : val),
            },
        },
        dataLabels: {
            enabled: true,
            style: { fontSize: '12px' },
            formatter: (text, op) => [text, FormateCurrentcy(allZero ? 0 : op.value)],
        },
        plotOptions: {
            treemap: {
                enableShades: true,
                shadeIntensity: 0.2,
                reverseNegativeShade: true,
                colorScale: {
                    ranges: [
                        { from: -10000000, to: 100000000, color: '#CD363A' },
                        { from: 0, to: 1000000000, color: '#52B12C' },
                    ],
                },
            },
        },
    }), [allZero]);

    const canRender = chartWidth > 0 && plotHeight > 0;

    return (
        <Box sx={{
            height: boxHeight,
            minHeight: boxHeight,
            maxHeight: boxHeight,
            width: '100%',
            overflow: 'hidden',
        }}>
            <Card
                elevation={0}
                sx={{
                    bgcolor: 'primary.light',
                    height: '100%',
                    width: '100%',
                    padding: 1,
                    boxShadow: '0px 0px 2px #fff',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                }}
            >
                <Box ref={containerRef} sx={{ width: '100%', height: plotHeight, overflow: 'hidden' }}>
                    {canRender ? (
                        <Chart
                            id="treemap"
                            height={plotHeight}
                            width={chartWidth}
                            series={chartSeries}
                            options={options}
                            type="treemap"
                        />
                    ) : null}
                </Box>
            </Card>
        </Box>
    );
}
